import SEO from '@/components/SEO';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileSlider from '@/components/MobileSlider';
import { Shield, Users, Award, Target, CheckCircle, TrendingUp } from 'lucide-react';

const AboutUs = () => {
  const stats = [
    { icon: Users, value: '5,000+', label: 'Satisfied Clients' },
    { icon: CheckCircle, value: '10,000+', label: 'Trademarks Registered' },
    { icon: Award, value: '98%', label: 'Success Rate' },
    { icon: TrendingUp, value: '15+', label: 'Years Experience' }
  ];

  const values = [
    {
      icon: Shield,
      title: 'Trust & Integrity',
      description: 'We prioritize transparency and honesty in every client interaction, building lasting relationships based on trust.'
    },
    {
      icon: Target,
      title: 'Excellence',
      description: 'Our commitment to quality ensures that every trademark application receives meticulous attention and expert handling.'
    },
    {
      icon: Users,
      title: 'Client-Focused',
      description: 'Your success is our success. We provide personalized support and guidance throughout your trademark journey.'
    },
    {
      icon: Award,
      title: 'Results-Driven',
      description: 'With a 98% success rate, we deliver proven results that protect and strengthen your brand identity.'
    }
  ];

  return (
    <>
      <SEO 
        title="About Us - Trusted Trademark & IP Legal Services"
        description="Learn about Trademark Scribe, a leading trademark and intellectual property law firm with 15+ years of experience, 98% success rate, and 5,000+ satisfied clients nationwide."
        keywords="trademark lawyers, IP attorneys, intellectual property law firm, trademark registration experts, about us"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-12 md:py-16 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(239,68,68,0.1),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(249,115,22,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-block mb-4">
              <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                About Trademark Scribe
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="block">Your Trusted Partner in</span>
              <span className="block mt-2 bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent">
                Intellectual Property Protection
              </span>
            </h1>
            
            <p className="text-lg text-gray-300 leading-relaxed">
              With over 15 years of experience and a proven track record, we've helped thousands of businesses protect their most valuable assets.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <MobileSlider cols={4}>
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="text-center h-full">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-600 to-orange-500 rounded-2xl mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              );
            })}
          </MobileSlider>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">Our Story</h2>
            <div className="space-y-6 text-gray-700 leading-relaxed text-lg">
              <p>
                Founded with a vision to make trademark protection accessible to businesses of all sizes, Trademark Scribe has grown to become one of the most trusted names in intellectual property services. Our journey began with a simple belief: every business deserves expert legal protection for their brand.
              </p>
              <p>
                Over the years, we've built a reputation for excellence, combining legal expertise with personalized service. Our team of experienced attorneys and trademark specialists work tirelessly to ensure that your intellectual property receives the protection it deserves.
              </p>
              <p>
                Today, we're proud to have helped over 5,000 clients successfully register and protect their trademarks, copyrights, and patents. Our industry-leading 98% success rate speaks to our commitment to excellence and attention to detail.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <MobileSlider cols={4}>
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <div key={idx} className="text-center p-6 h-full">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-600 to-orange-500 rounded-2xl mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </MobileSlider>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Why Choose Trademark Scribe?</h2>
            <p className="text-xl text-gray-300 mb-12">
              We combine legal expertise, personalized service, and proven results to deliver exceptional value
            </p>
            <MobileSlider>
              <div className="h-full">
                <div className="text-5xl font-bold bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent mb-3">15+</div>
                <p className="text-gray-300">Years of experience protecting brands nationwide</p>
              </div>
              <div className="h-full">
                <div className="text-5xl font-bold bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent mb-3">98%</div>
                <p className="text-gray-300">Success rate for trademark applications</p>
              </div>
              <div className="h-full">
                <div className="text-5xl font-bold bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent mb-3">24/7</div>
                <p className="text-gray-300">Dedicated support for all our clients</p>
              </div>
            </MobileSlider>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default AboutUs;